import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/use-toast'
import {
	Edit,
	Play,
	Pause,
	Trash2,
	MoreVertical,
	Star,
	Layout,
	MessagesSquare,
	Bug,
	Lightbulb,
	Code,
	Eye,
	Loader2,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { motion } from 'framer-motion'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible'
import MetricCard from '@/components/dashboard/MetricCard'
import WidgetTimeSeriesChart from '@/components/dashboard/WidgetTimeSeriesChart'
import WidgetNPSChart from '@/components/dashboard/WidgetNPSChart'
import WidgetCSATDistributionChart from '@/components/dashboard/WidgetCSATDistributionChart'
import { EmbedWidgetContainer } from '@/components/widget/EmbedWidget'
import { WidgetInstallation } from '@/components/widget/WidgetInstallation'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import { getWidgetDetails, type ApiWidget } from '@/lib/api'
import { getWidgetStats, getRecentActivity } from '@/pages/api/widgetDetails'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTeamSubscription } from '@/hooks/useTeamSubscription'
import type { Response as WidgetResponse } from '@/lib/widget-schema'
import { widgetApi } from '@/services/api'
import { BugReportTable } from '@/components/bug-reports/BugReportTable'
import { FeatureRequestGrid } from '@/components/feature-requests/FeatureRequestGrid'
import TimePeriodSelector from '@/components/dashboard/TimePeriodSelector'
import RecentActivityResponseTable from '@/components/dashboard/RecentActivityResponseTable'
import { BugReportDetailView } from '@/components/bug-reports/BugReportDetailView'
import { FeatureRequestDetailView } from '@/components/feature-requests/FeatureRequestDetailView'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { useTranslation } from '@/hooks/useTranslation'

// --- Types ---
type TimePeriod = 'all' | 'week' | 'month' | 'year'

interface WidgetStats {
	totalResponses: number
	npsScore?: number
	avgRating?: number
	bugsReported: number
	featuresSuggested: number
	reviewsSubmitted: number
}

interface UserMetadata {
	device: string | null
	browser: string | null
	location: string | null
	pageUrl?: string | null
	language?: string | null
	referrer?: string | null
	timezone?: string | null
	pageTitle?: string | null
	timestamp?: string | null
	userAgent?: string | null
	screenSize?: {
		width: number
		height: number
	} | null
}

type RecentResponse = WidgetResponse & {
	widgets: {
		name: string
		type: string
		question: string
	}
}

interface RecentBug {
	id: string
	created_at: string
	status: string
	severity: string
	description: string
}

interface RecentFeature {
	id: string
	created_at: string
	title: string
	upvote_count: number
	status: string
}

interface RecentReview {
	id: string
	created_at: string
	score: number
	review_text: string
}

// --- Skeleton Component ---
const WidgetDetailsSkeleton = () => (
	<div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6 animate-pulse">
		<Skeleton className="h-6 w-1/3 mb-6 rounded-md" /> {/* Breadcrumb */}
		<Card className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
			<div className="flex-1 space-y-3">
				<Skeleton className="h-8 w-3/5 rounded" />
				<Skeleton className="h-4 w-20 rounded-full" />
			</div>
			<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
				<Skeleton className="h-9 w-full sm:w-32 rounded-md" />
				<div className="flex gap-2">
					<Skeleton className="h-9 w-9 rounded-md" />
					<Skeleton className="h-9 w-9 rounded-md" />
					<Skeleton className="h-9 w-9 rounded-md" />
					<Skeleton className="h-9 w-9 rounded-md" />
					<Skeleton className="h-9 w-9 rounded-md" />
				</div>
			</div>
		</Card>
		{/* Metrics */}
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
			{[...Array(4)].map((_, i) => (
				<Skeleton key={i} className="h-32 rounded-lg" />
			))}
		</div>
		{/* Analytics */}
		<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
			<Skeleton className="h-80 rounded-lg" />
			<Skeleton className="h-80 rounded-lg" />
		</div>
		{/* Recent Activity */}
		<Card>
			<CardHeader>
				<Skeleton className="h-6 w-1/4 rounded" />
			</CardHeader>
			<CardContent className="space-y-4 p-6">
				<Skeleton className="h-10 w-full rounded-md" />{' '}
				{/* TabsList Skeleton */}
				<Skeleton className="h-12 w-full rounded-md" />
				<Skeleton className="h-12 w-full rounded-md" />
				<Skeleton className="h-12 w-full rounded-md" />
			</CardContent>
		</Card>
	</div>
)

const WidgetDetails = () => {
	const { t } = useTranslation()
	const { id: widgetId } = useParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { currentProgramAccessLevel } = useProgramContext()
	const [timePeriod, setTimePeriod] = useState<TimePeriod>('all')
	const [showTestWidget, setShowTestWidget] = useState(false)
	const [isInstallCodeOpen, setIsInstallCodeOpen] = useState(false)
	const [selectedBugId, setSelectedBugId] = useState<string | null>(null)
	const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
		null
	)
	const { teamId } = useTeamContext()
	const { data: subscriptionData, isLoading: isSubscriptionLoading } =
		useTeamSubscription(teamId)

	const isProUser =
		!isSubscriptionLoading && (subscriptionData?.isPremium || false)

	// --- Queries ---
	const {
		data: widget,
		isLoading: isWidgetLoading,
		error: widgetError,
		refetch: refetchWidget,
	} = useQuery<ApiWidget, Error>({
		queryKey: ['widget', widgetId],
		queryFn: () =>
			widgetId
				? getWidgetDetails(widgetId)
				: Promise.reject(new Error('Widget ID missing')),
		enabled: !!widgetId,
	})

	const { data: widgetStats, isLoading: isStatsLoading } = useQuery<
		WidgetStats | null,
		Error
	>({
		queryKey: ['widgetStats', widgetId, teamId, timePeriod],
		queryFn: () =>
			widgetId && teamId ? getWidgetStats(widgetId, teamId, timePeriod) : null,
		enabled: !!widgetId && !!teamId,
	})

	// --- Recent Activity Queries ---
	const fetchRecentData = <
		T extends RecentResponse | RecentBug | RecentFeature | RecentReview
	>(
		dataType: string,
		enabled: boolean
	) => {
		return useQuery<T[], Error>({
			queryKey: [`recentWidgetData`, widgetId, teamId, dataType],
			queryFn: () =>
				widgetId && teamId
					? getRecentActivity(widgetId, teamId, dataType as any, 5)
					: [],
			enabled: !!widgetId && !!teamId && enabled,
			staleTime: 60 * 1000, // Refetch recent data every minute
		})
	}

	const { data: recentResponses, isLoading: isResponsesLoading } =
		fetchRecentData<RecentResponse>('responses', !!widget?.enable_survey)
	const { data: recentBugs, isLoading: isBugsLoading } =
		fetchRecentData<RecentBug>('bugs', !!widget?.enable_bug_reporting)
	const { data: recentFeatures, isLoading: isFeaturesLoading } =
		fetchRecentData<RecentFeature>(
			'features',
			!!widget?.enable_feature_requests
		)
	const { data: recentReviews, isLoading: isReviewsLoading } =
		fetchRecentData<RecentReview>('reviews', !!widget?.enable_reviews)

	// --- Mutations ---
	const toggleWidgetActive = useMutation<any, Error, { isActive: boolean }>({
		mutationFn: async ({ isActive }) => {
			if (!widgetId || !widget) throw new Error(t('widgetDetails.widgetDataNotAvailable'))
			if (isActive && !isProUser) {
				const limitData = await widgetApi.checkActivationLimits(
					widget.team_id,
					widgetId
				)
				if (!limitData.canActivate) {
					return { showUpgradeModal: true, message: limitData.message }
				}
			}
			return widgetApi.updateWidget(widgetId, { is_active: isActive })
		},
		onSuccess: (result) => {
			if (result?.showUpgradeModal) {
				toast({
					title: t('widgetDetails.upgradeRequired'),
					description: result.message,
					variant: 'destructive',
				})
			} else {
				refetchWidget()
				queryClient.invalidateQueries({
					queryKey: ['widgets', widget?.program_id],
				})
				toast({ title: t('widgetDetails.success'), description: t('widgetDetails.widgetStatusUpdated') })
			}
		},
		onError: (error) => {
			toast({
				title: t('widgetDetails.error'),
				description: error.message || t('widgetDetails.failedToUpdateStatus'),
				variant: 'destructive',
			})
		},
	})

	const deleteWidgetMutation = useMutation<void, Error, void>({
		mutationFn: async () => {
			if (!widgetId) throw new Error(t('widgetDetails.widgetIdMissing'))
			await widgetApi.deleteWidget(widgetId)
		},
		onSuccess: () => {
			toast({ title: t('widgetDetails.success'), description: t('widgetDetails.widgetDeleted') })
			queryClient.invalidateQueries({
				queryKey: ['widgets', widget?.program_id],
			})
			navigate('/widgets')
		},
		onError: (error) => {
			toast({
				title: t('widgetDetails.error'),
				description: error.message || t('widgetDetails.failedToDeleteWidget'),
				variant: 'destructive',
			})
		},
	})

	// --- Loading / Error ---
	if (isWidgetLoading || isSubscriptionLoading) {
		return <WidgetDetailsSkeleton />
	}
	if (widgetError) {
		return (
			<div className="p-8 text-center text-destructive">
				{t('widgetDetails.errorLoadingWidget')}: {widgetError.message}
				<Button
					onClick={() => refetchWidget()}
					variant="outline"
					size="sm"
					className="ml-2">
					{t('widgetDetails.retry')}
				</Button>
			</div>
		)
	}
	if (!widget) {
		return (
			<div className="p-8 text-center text-muted-foreground">
				{t('widgetDetails.widgetNotFound')}
			</div>
		)
	}

	// --- Determine Active Modules ---
	const activeModules = [
		widget.enable_survey && {
			key: 'responses',
			label: t('widgetDetails.responses'),
			icon: MessagesSquare,
			data: recentResponses,
			loading: isResponsesLoading,
			table: RecentActivityResponseTable,
			linkPath: 'responses',
		},
		widget.enable_bug_reporting && {
			key: 'bugs',
			label: t('widgetDetails.bugReports'),
			icon: Bug,
			data: recentBugs,
			loading: isBugsLoading,
			table: BugReportTable,
			linkPath: 'bugs',
			onSelect: setSelectedBugId,
		},
		widget.enable_feature_requests && {
			key: 'features',
			label: t('widgetDetails.features'),
			icon: Lightbulb,
			data: recentFeatures,
			loading: isFeaturesLoading,
			table: FeatureRequestGrid,
			linkPath: 'features',
			onSelect: setSelectedFeatureId,
		},
		widget.enable_reviews && {
			key: 'reviews',
			label: t('widgetDetails.reviews'),
			icon: Star,
			data: recentReviews,
			loading: isReviewsLoading,
			table: RecentActivityResponseTable,
			linkPath: 'reviews',
		},
	].filter(Boolean) as {
		key: string
		label: string
		icon: React.ElementType
		data: any[] | undefined
		loading: boolean
		table: React.ElementType
		linkPath: string
		onSelect?: (id: string | null) => void
	}[]

	const defaultTab =
		activeModules.length > 0 ? activeModules[0].key : 'responses'

	// Calculate metrics from responses
	const calculateMetrics = () => {
		if (!recentResponses?.length || !widget) {
			return {
				npsScore: 0,
				responseRate: '0%',
				averageRating: 0,
			}
		}

		// Calculate response rate (assuming 100 views per response for demo)
		const responseRate = `${Math.round((recentResponses.length / 100) * 100)}%`

		// Calculate metrics based on widget type
		if (widget.type === 'nps') {
			const npsScore = Math.round(
				(recentResponses.filter((r) => r.score >= 9).length /
					recentResponses.length) *
					100 -
					(recentResponses.filter((r) => r.score <= 6).length /
						recentResponses.length) *
						100
			)
			return {
				npsScore,
				responseRate,
				averageRating: 0,
			}
		} else if (widget.type === 'csat') {
			const averageRating =
				Math.round(
					(recentResponses.reduce((acc, r) => acc + (r.score || 0), 0) /
						recentResponses.length) *
						10
				) / 10
			return {
				npsScore: 0,
				responseRate,
				averageRating,
			}
		} else {
			// For open-ended questions
			return {
				npsScore: 0,
				responseRate,
				averageRating: 0,
			}
		}
	}

	const metrics = calculateMetrics()

	return (
		<div className="min-h-screen">
			<div className="p-4 md:p-8 max-w-7xl mx-auto">
				<Breadcrumb className="mb-6" />

				{/* --- 1. Header Section --- */}
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					className="mb-6 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-4 sm:p-6 shadow-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-3 mb-1">
							<div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
								<Layout className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
							</div>
							<h1
								className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent truncate"
								title={widget.name}>
								{widget.name}
							</h1>
						</div>
						<Badge
							variant={widget.is_active ? 'default' : 'secondary'}
							className={`ml-11 sm:ml-12 ${
								widget.is_active
									? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
									: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
							}`}>
							<div
								className={`h-2 w-2 rounded-full mr-1.5 ${
									widget.is_active ? 'bg-green-500' : 'bg-gray-400'
								}`}></div>
							{widget.is_active ? t('widgetDetails.active') : t('widgetDetails.paused')}
						</Badge>
					</div>

					<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto mt-4 sm:mt-0">
						<TimePeriodSelector
							selectedPeriod={timePeriod}
							onChange={setTimePeriod}
							className="w-full sm:w-auto"
						/>
						<div className="flex gap-2 w-full sm:w-auto">
							{currentProgramAccessLevel !== 'read' && (
								<Button
									variant="outline"
									onClick={() => navigate(`/widgets/edit/${widgetId}`)}
									size="sm"
									className="flex-1 sm:flex-none">
									<Edit className="h-4 w-4 sm:mr-1" />
									<span className="hidden sm:inline">{t('widgetDetails.edit')}</span>
								</Button>
							)}
							<Button
								variant="outline"
								size="sm"
								className="flex-1 sm:flex-none"
								onClick={() => setIsInstallCodeOpen(!isInstallCodeOpen)}>
								<Code className="h-4 w-4 sm:mr-1" />
								<span className="hidden sm:inline">{t('widgetDetails.code')}</span>
							</Button>
							<Button
								variant="outline"
								onClick={() => setShowTestWidget(!showTestWidget)}
								size="sm"
								className="flex-1 sm:flex-none">
								<Eye className="h-4 w-4 sm:mr-1" />
								<span className="hidden sm:inline">{t('widgetDetails.test')}</span>
							</Button>

							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline" size="sm">
										<MoreVertical className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										onClick={() =>
											toggleWidgetActive.mutate({ isActive: !widget.is_active })
										}
										disabled={toggleWidgetActive.isPending}>
										{widget.is_active ? (
											<Pause className="mr-2 h-4 w-4" />
										) : (
											<Play className="mr-2 h-4 w-4" />
										)}
										{widget.is_active ? t('widgetDetails.pauseWidget') : t('widgetDetails.activateWidget')}
									</DropdownMenuItem>
									{currentProgramAccessLevel !== 'read' && (
										<>
											<DropdownMenuSeparator />
											<AlertDialog>
												<AlertDialogTrigger asChild>
													<DropdownMenuItem
														onSelect={(e) => e.preventDefault()}
														className="text-destructive focus:bg-destructive/10 focus:text-destructive">
														<Trash2 className="mr-2 h-4 w-4" /> {t('widgetDetails.deleteWidget')}
													</DropdownMenuItem>
												</AlertDialogTrigger>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>{t('widgetDetails.deleteWidgetQuestion')}</AlertDialogTitle>
														<AlertDialogDescription>
															{t('widgetDetails.actionCannotBeUndone')}
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel>{t('widgetDetails.cancel')}</AlertDialogCancel>
														<AlertDialogAction
															onClick={() => deleteWidgetMutation.mutate()}
															className="bg-destructive hover:bg-destructive/90"
															disabled={deleteWidgetMutation.isPending}>
															{deleteWidgetMutation.isPending ? (
																<Loader2 className="mr-2 h-4 w-4 animate-spin" />
															) : null}{' '}
															{t('widgetDetails.delete')}
														</AlertDialogAction>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										</>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>
				</motion.div>

				{/* Installation Code Collapsible Content */}
				<Collapsible
					open={isInstallCodeOpen}
					onOpenChange={setIsInstallCodeOpen}>
					<CollapsibleContent>
						<motion.div
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: 'auto', opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
							className="mb-6 overflow-hidden">
							<Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-slate-100 p-6 shadow-inner">
								<CardTitle className="text-lg mb-4 text-slate-100">
									{t('widgetDetails.installationAndSetup')}
								</CardTitle>
								<WidgetInstallation widgetId={widgetId!} />
								<CardDescription className="mt-4 text-slate-400 text-sm">
									{t('widgetDetails.needHelp')}{' '}
									<Link to="/docs" className="underline hover:text-slate-200">
										{t('widgetDetails.viewInstallationGuide')}
									</Link>{' '}
									{t('widgetDetails.or')}{' '}
									<a
										href={`mailto:contact@feedbask.com?subject=Help%20installing%20widget%20${widgetId}`}
										className="underline hover:text-slate-200">
										{t('widgetDetails.contactSupport')}
									</a>
									.
								</CardDescription>
							</Card>
						</motion.div>
					</CollapsibleContent>
				</Collapsible>

				{/* --- 2. Key Metrics Section --- */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}
					className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
					{isStatsLoading ? (
						<>
							{' '}
							<Skeleton className="h-32 rounded-lg" />{' '}
							<Skeleton className="h-32 rounded-lg" />{' '}
							<Skeleton className="h-32 rounded-lg" />{' '}
							<Skeleton className="h-32 rounded-lg" />{' '}
						</>
					) : widgetStats ? (
						<>
							<MetricCard
								title={t('widgetDetails.totalSubmissions')}
								value={widgetStats.totalResponses}
								description={t('widgetDetails.lastPeriod', { period: timePeriod })}
								icon={<MessagesSquare className="h-5 w-5" />}
							/>
							{widgetStats.npsScore !== undefined && (
								<MetricCard
									title={t('widgetDetails.npsScore')}
									value={
										widgetStats.npsScore > 0
											? `+${widgetStats.npsScore}`
											: widgetStats.npsScore
									}
									description={t('widgetDetails.lastPeriod', { period: timePeriod })}
									icon={<Star className="h-5 w-5" />}
								/>
							)}
							{widgetStats.avgRating !== undefined && (
								<MetricCard
									title={t('widgetDetails.avgRating')}
									value={widgetStats.avgRating}
									description={t('widgetDetails.outOfFiveLastPeriod', { period: timePeriod })}
									icon={<Star className="h-5 w-5" />}
								/>
							)}
							{widget.enable_bug_reporting && (
								<MetricCard
									title={t('widgetDetails.bugsReported')}
									value={widgetStats.bugsReported}
									description={t('widgetDetails.lastPeriod', { period: timePeriod })}
									icon={<Bug className="h-5 w-5" />}
								/>
							)}
							{widget.enable_feature_requests && (
								<MetricCard
									title={t('widgetDetails.featuresSuggested')}
									value={widgetStats.featuresSuggested}
									description={t('widgetDetails.lastPeriod', { period: timePeriod })}
									icon={<Lightbulb className="h-5 w-5" />}
								/>
							)}
							{widget.enable_reviews && (
								<MetricCard
									title={t('widgetDetails.reviewsSubmitted')}
									value={widgetStats.reviewsSubmitted}
									description={t('widgetDetails.lastPeriod', { period: timePeriod })}
									icon={<Star className="h-5 w-5" />}
								/>
							)}
						</>
					) : (
						<Card className="col-span-full p-6 text-center text-muted-foreground">
							{t('widgetDetails.couldNotLoadKeyMetrics')}
						</Card>
					)}
				</motion.div>

				{/* --- 3. Analytics Section --- */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2 }}
					className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
					<WidgetTimeSeriesChart widgetId={widgetId!} />
					{widget.type === 'nps' && <WidgetNPSChart widgetId={widgetId!} />}
					{widget.type === 'csat' && (
						<WidgetCSATDistributionChart widgetId={widgetId!} />
					)}
				</motion.div>

				{/* --- 4. Recent Activity Section --- */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3 }}>
					<Card className="overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
						<CardHeader className="border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900">
							<CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
								{t('widgetDetails.recentActivity')}
							</CardTitle>
							<CardDescription className="text-sm text-gray-600 dark:text-gray-400">
								{t('widgetDetails.latestEntriesSubmitted')}
							</CardDescription>
						</CardHeader>
						{activeModules.length > 0 ? (
							<Tabs defaultValue={defaultTab} className="w-full">
								<TabsList className="h-auto p-0 bg-transparent border-b border-gray-200 dark:border-gray-700 rounded-none justify-start w-full">
									{activeModules.map((mod) => (
										<TabsTrigger
											key={mod.key}
											value={mod.key}
											className="flex items-center gap-2.5 px-6 py-4 h-auto rounded-none border-b-2 border-transparent bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50 data-[state=active]:border-primary data-[state=active]:bg-primary/5 data-[state=active]:text-primary data-[state=active]:shadow-none transition-all duration-200 font-medium text-gray-600 dark:text-gray-400 data-[state=active]:text-primary -mb-px">
											<mod.icon className="h-4 w-4" /> 
											<span>{mod.label}</span>
										</TabsTrigger>
									))}
								</TabsList>
								{activeModules.map((mod) => {
									const TableComponent = mod.table
									const dataKey =
										mod.key === 'responses' || mod.key === 'reviews'
											? 'responses'
											: mod.key === 'bugs'
											? 'bugs'
											: mod.key === 'features'
											? 'features'
											: 'items'
									const recentData = mod.data
									const loadingState = mod.loading
									const selectHandler = mod.onSelect

									return (
										<TabsContent
											key={mod.key}
											value={mod.key}
											className="p-0 pt-0 mt-0 focus-visible:outline-none focus-visible:ring-0">
											<div className="max-h-[450px] overflow-y-auto bg-white dark:bg-gray-900">
												{loadingState ? (
													<div className="space-y-3 p-6">
														<Skeleton className="h-12 w-full rounded-lg" />
														<Skeleton className="h-12 w-full rounded-lg" />
														<Skeleton className="h-12 w-full rounded-lg" />
														<Skeleton className="h-12 w-full rounded-lg" />
													</div>
												) : recentData && recentData.length > 0 ? (
													<TableComponent
														{...{ [dataKey]: recentData }}
														isLoading={loadingState}
														{...(mod.key === 'bugs' && {
															onBugSelect: selectHandler,
														})}
														{...(mod.key === 'features' && {
															onFeatureSelect: selectHandler,
														})}
													/>
												) : (
													<div className="text-center py-12">
														<div className="w-12 h-12 mx-auto mb-4 text-gray-400">
															<mod.icon className="w-full h-full" />
														</div>
														<p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
															{t('widgetDetails.noRecentFound', { label: mod.label.toLowerCase() })}
														</p>
														<p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
															{t('widgetDetails.willAppearHere', { label: mod.label })}
														</p>
													</div>
												)}
											</div>
											{recentData && recentData.length > 0 && (
												<div className="p-4 text-center border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
													<Link
														to={`/${mod.linkPath}?widgetId=${widgetId}`}
														className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium transition-colors duration-200">
														<span>{t('widgetDetails.viewAllForThisWidget', { label: mod.label })}</span>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
														</svg>
													</Link>
												</div>
											)}
										</TabsContent>
									)
								})}
							</Tabs>
						) : (
							<CardContent className="p-12 text-center bg-white dark:bg-gray-900">
								<div className="w-16 h-16 mx-auto mb-4 text-gray-400">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
										<path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 1.16.21 2.34.21 3.5 0 5.16-1 9-5.45 9-11V7l-10-5z"/>
										<path d="M9 12l2 2 4-4"/>
									</svg>
								</div>
								<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
									{t('widgetDetails.noFeaturesEnabled')}
								</h3>
								<p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">
									{t('widgetDetails.enableFeaturesDescription')}
								</p>
							</CardContent>
						)}
					</Card>
				</motion.div>

				{/* --- 5. Test Widget Display --- */}
				{showTestWidget && widget && (
					<div className="fixed bottom-4 right-4 z-[1000] transition-all duration-300 ease-out">
						<EmbedWidgetContainer
							widgetId={widgetId!}
							initialWidgetData={widget}
							onRemove={() => setShowTestWidget(false)}
							showRemoveButton={true}
						/>
					</div>
				)}

				{/* --- Detail Views (Modals/Drawers) --- */}
				{selectedBugId && (
					<BugReportDetailView
						bugId={selectedBugId}
						onClose={() => setSelectedBugId(null)}
					/>
				)}
				{selectedFeatureId && (
					<FeatureRequestDetailView
						featureId={selectedFeatureId}
						onClose={() => setSelectedFeatureId(null)}
					/>
				)}
			</div>
		</div>
	)
}

export default WidgetDetails
